import React from 'react'
import {
    Box,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import {
    FormData,
    FormDataValue,
    Option,
    QuestionType,
    RowData,
    UploadedDocumentContent,
} from '../types'
import {IUserInfo} from '../../../../types/user'
import {DocumentApi} from '../../../../services/DocumentApi.service'
import DocumentUploadQuestion from './DocumentUploadQuestion'
import TableQuestion from './TableQuestion'

interface Colors {
    primariaEscura: string
    botaoFormulario: string
}

interface QuestionRendererProps {
    question: QuestionType
    projectId: string
    formData: FormData
    setFormData: React.Dispatch<React.SetStateAction<FormData>>
    onChange: (id: string, value: FormDataValue) => void
    onDocumentUploadPendingChange: (id: string, pending: boolean) => void
    onProcessUpload?: (id: string, documents: UploadedDocumentContent[]) => void
    onNext: () => void
    isCurrentQuestionAnswered: () => boolean
    api: DocumentApi
    newTables: RowData[][]
    onAddTable: () => void
    onAddRow: (tableIndex: number) => void
    onDeleteTable: (tableIndex: number) => void
    onDeleteRow: (tableIndex: number, rowId: number) => void
    onInputChangeTable: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        tableIndex: number,
        rowIndex: number,
        field: keyof RowData
    ) => void
    calculateTableTotal: (table: RowData[]) => number
    calculateGlobalTotal: () => number
    formatCurrency: (value: number) => string
    colors: Colors
    user: IUserInfo | null
}

export default function QuestionRenderer({
    question,
    projectId,
    formData,
    setFormData,
    onChange,
    onDocumentUploadPendingChange,
    onProcessUpload,
    onNext,
    isCurrentQuestionAnswered,
    api,
    newTables,
    onAddTable,
    onAddRow,
    onDeleteTable,
    onDeleteRow,
    onInputChangeTable,
    calculateTableTotal,
    calculateGlobalTotal,
    formatCurrency,
    colors,
    user,
}: QuestionRendererProps) {
    const selectQuestionRef = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
        if (question.question_type !== 'select') {
            return
        }

        const timerId = window.setTimeout(() => {
            selectQuestionRef.current?.focus()
        }, 0)

        return () => window.clearTimeout(timerId)
    }, [question.id, question.question_type])

    if (question.question_type === 'document_upload') {
        const currentValue = Array.isArray(formData[question.id])
            ? (formData[question.id] as UploadedDocumentContent[])
            : question.answer_content?.documents || []

        return (
            <DocumentUploadQuestion
                question={question}
                projectId={projectId || question.project_id}
                value={currentValue}
                api={api}
                colors={colors}
                onChange={onChange}
                onPendingChange={onDocumentUploadPendingChange}
                onProcessUpload={onProcessUpload}
            />
        )
    }

    if (question.type.text || question.question_type === 'text') {
        const getInitialValue = () => {
            const questionId = question.id
            const existingFormData = formData[questionId]
            const answerContent = question.answer_content?.text

            if (existingFormData !== undefined) {
                return existingFormData
            }

            if (answerContent) {
                setTimeout(() => onChange(questionId, answerContent), 0)
                return answerContent
            }

            if (
                question.content === 'Qual é a entidade contratante?' &&
                user?.organization?.name
            ) {
                const orgName = user.organization.name

                setTimeout(() => onChange(questionId, orgName), 0)
                return orgName
            }

            return ''
        }

        return (
            <TextField
                onKeyDown={(e) => {
                    if (
                        e.key === 'Enter' &&
                        !e.shiftKey &&
                        isCurrentQuestionAnswered()
                    ) {
                        onNext()
                    }
                }}
                fullWidth
                placeholder={question.content}
                variant="outlined"
                value={getInitialValue()}
                onChange={(e) => onChange(question.id, e.target.value)}
                multiline
                InputLabelProps={{shrink: false}}
                autoFocus
            />
        )
    }

    if (question.type.radio) {
        return (
            <Box sx={{mb: 2}}>
                <Typography variant="subtitle1" sx={{mb: 1}}>
                    {question.content}
                </Typography>
                <RadioGroup
                    value={formData[question.id] || ''}
                    onChange={(e) => onChange(question.id, e.target.value)}
                >
                    {question.type.radio.options.map((option) => (
                        <FormControlLabel
                            key={option.index}
                            value={option.content}
                            control={<Radio />}
                            label={option.content}
                        />
                    ))}
                </RadioGroup>
            </Box>
        )
    }

    if (question.question_type === 'select' && question.type.select) {
        const handleSelectKeyDown = (e: React.KeyboardEvent) => {
            if (
                e.key === 'Enter' &&
                !e.shiftKey &&
                isCurrentQuestionAnswered()
            ) {
                onNext()
                return
            }

            if (!/^[1-9]$/.test(e.key)) {
                return
            }

            const optionIndex = Number(e.key) - 1
            const option = question.type.select?.options[optionIndex]

            if (!option) {
                return
            }

            e.preventDefault()
            onChange(question.id, option.content)
        }

        return (
            <Stack
                ref={selectQuestionRef}
                direction="column"
                spacing={2}
                onKeyDown={handleSelectKeyDown}
                tabIndex={-1}
            >
                {question.type.select.options.map((option) => (
                    <ToggleButtonGroup
                        key={option.index}
                        value={
                            formData[question.id] !== undefined
                                ? formData[question.id]
                                : question.answer_content?.text || ''
                        }
                        exclusive
                        onChange={(e, value) => onChange(question.id, value)}
                        aria-label="select options"
                        sx={{
                            width: '100%',
                        }}
                    >
                        <ToggleButton
                            value={option.content}
                            aria-label={option.content}
                            fullWidth
                            sx={{
                                border: '1px solid #012441',
                                color: '#012441',
                                backgroundColor: 'transparent',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                marginBottom: '8px',
                                '&:last-child': {
                                    marginBottom: 0,
                                },
                                '&.Mui-selected': {
                                    backgroundColor: colors.botaoFormulario,
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: colors.botaoFormulario,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: `${colors.botaoFormulario}20`,
                                },
                            }}
                        >
                            {option.title}
                        </ToggleButton>
                    </ToggleButtonGroup>
                ))}
            </Stack>
        )
    }

    if (question.question_type === 'multiselect') {
        const otherOptionContent = 'Outros...'
        const isOtherSelected =
            Array.isArray(formData[question.id]) &&
            (formData[question.id] as string[]).some(
                (v) =>
                    v === otherOptionContent ||
                    !question.type.multiselect?.options.find(
                        (opt) => opt.content === v
                    )
            )

        const getOtherValue = () => {
            if (!Array.isArray(formData[question.id])) return ''
            const values = formData[question.id] as string[]
            const customValue = values.find(
                (v) =>
                    !question.type.multiselect?.options.find(
                        (opt) => opt.content === v
                    )
            )
            return customValue && customValue !== otherOptionContent
                ? customValue
                : ''
        }

        const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const customValue = e.target.value
            const currentValues = (formData[question.id] as string[]) || []
            const otherOption = question.type.multiselect?.options.find(
                (opt) => opt.title === otherOptionContent
            )

            const newValues = currentValues.filter(
                (v) =>
                    question.type.multiselect?.options.find(
                        (opt) => opt.content === v
                    ) &&
                    (!otherOption || v !== otherOption.content)
            )

            if (customValue) {
                newValues.push(customValue)
            } else if (otherOption) {
                newValues.push(otherOption.content)
            }

            setFormData((prev) => ({...prev, [question.id]: newValues}))
        }

        return (
            <Stack
                direction="column"
                spacing={0}
                onKeyDown={(e) => {
                    if (
                        e.key === 'Enter' &&
                        !e.shiftKey &&
                        isCurrentQuestionAnswered()
                    ) {
                        onNext()
                    }
                }}
                tabIndex={-1}
            >
                {question.type.multiselect?.options.map((option: Option) => (
                    <Box
                        key={option.index}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        {option.title === otherOptionContent ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isOtherSelected}
                                        onChange={() =>
                                            onChange(
                                                question.id,
                                                option.content
                                            )
                                        }
                                    />
                                }
                                label={
                                    <TextField
                                        variant="standard"
                                        placeholder="Outros..."
                                        value={getOtherValue()}
                                        onChange={handleOtherChange}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            minWidth: '200px',
                                            '& .MuiInput-underline:before': {
                                                borderBottom:
                                                    '1px solid rgba(0, 0, 0, 0.42)',
                                            },
                                        }}
                                    />
                                }
                                sx={{width: '100%'}}
                            />
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            Array.isArray(
                                                formData[question.id]
                                            ) &&
                                            (
                                                formData[
                                                    question.id
                                                ] as string[]
                                            ).includes(option.content)
                                        }
                                        onChange={() =>
                                            onChange(
                                                question.id,
                                                option.content
                                            )
                                        }
                                    />
                                }
                                label={option.title.replace(/\.$/, '')}
                            />
                        )}
                    </Box>
                ))}
            </Stack>
        )
    }

    if (question.type.table) {
        const handleTableKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (
                e.key === 'Enter' &&
                !e.shiftKey &&
                isCurrentQuestionAnswered()
            ) {
                e.preventDefault()
                onNext()
            }
        }

        return (
            <Box onKeyDown={handleTableKeyDown} tabIndex={-1}>
                <TableQuestion
                    question={question}
                    newTables={newTables}
                    onAddTable={onAddTable}
                    onAddRow={onAddRow}
                    onDeleteTable={onDeleteTable}
                    onDeleteRow={onDeleteRow}
                    onInputChangeTable={onInputChangeTable}
                    calculateTableTotal={calculateTableTotal}
                    calculateGlobalTotal={calculateGlobalTotal}
                    formatCurrency={formatCurrency}
                    colors={colors}
                />
            </Box>
        )
    }

    return null
}
